import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';

const ModuleContext = createContext();

export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
};

export const ModuleProvider = ({ children }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/feature-modules`);
      setModules(response.data);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch modules when token changes (e.g., after login)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchModules();
    } else {
      setModules([]);
      setLoading(false);
    }
  }, [fetchModules]);

  // Listen for storage events (login/logout in other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          fetchModules();
        } else {
          setModules([]);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchModules]);

  // Also expose a way for App.js to trigger refresh after login
  const refreshModulesOnLogin = useCallback(async () => {
    await fetchModules();
  }, [fetchModules]);

  const checkModuleAccess = useCallback((moduleId) => {
    const module = modules.find(m => m.id === moduleId);
    if (!module) return { hasAccess: false, isVisible: false, reason: 'not_found' };
    
    return {
      hasAccess: module.user_has_access,
      isEnabled: module.is_enabled,
      showTeaser: module.show_teaser,
      isVisible: module.is_enabled || module.show_teaser,  // Visible if enabled OR teaser
      requiredPlan: module.requires_plan,
      comingSoonText: module.coming_soon_text,
      module
    };
  }, [modules]);

  const getModule = useCallback((moduleId) => {
    return modules.find(m => m.id === moduleId);
  }, [modules]);

  const refreshModules = useCallback(() => {
    return fetchModules();
  }, [fetchModules]);

  return (
    <ModuleContext.Provider value={{
      modules,
      loading,
      checkModuleAccess,
      getModule,
      refreshModules,
      refreshModulesOnLogin
    }}>
      {children}
    </ModuleContext.Provider>
  );
};

export default ModuleContext;
